import {TemplateResponseData, useUpdateTemplate, useCreateTemplate, BaseTemplateData, useTemplate} from '@api'
import {FormFieldType, NavigationPathKey, TranslationKey} from '@enums'
import {errorAtom, isFormDirtyAtom, handleNavigateAtom, pageLinksAtom, getUserSelectionAtom} from '@state'

import {useEffect, useMemo, useState} from 'react'

import {
  contractTypeOptions,
  FormFieldItem,
  generateReadableErrorMessages,
  validateFields,
  generatePdf,
  useViewNavigate,
  getUserData,
  addTopRightPageNumbers,
} from 'common'
import {DropdownItem} from 'components/ui-kit'
import saveAs from 'file-saver'
import {useAtomValue, useSetAtom} from 'jotai'
import {useTranslation} from 'react-i18next'
import {useParams} from 'react-router-dom'

import placeholder from '@images/placeholder.png'

import {templateHtml} from './template-html'

/**
 * Initial data
 */
const initialContractTemplateData: TemplateResponseData = {
  id: '',
  name: '',
  htmlContent: '',
  companyId: '',
  description: '',
  contractType: '',
}

export function useContractTemplateFormModel() {
  const setError = useSetAtom(errorAtom)
  const setIsFormDirty = useSetAtom(isFormDirtyAtom)
  const handleNavigate = useSetAtom(handleNavigateAtom)
  const setTitleLinks = useSetAtom(pageLinksAtom)
  const [contractTemplateData, setContractTemplateData] = useState<TemplateResponseData>(initialContractTemplateData)
  const [contractTemplateFormFieldsErrors, setContractTemplateFormFieldsErrors] = useState<string[]>([])
  const [isUploadDisabled, setIsUploadDisabled] = useState<boolean>(true)
  const navigate = useViewNavigate()

  const currentUser = getUserData()
  const userSelections = useAtomValue(getUserSelectionAtom)
  const userSelection = userSelections[currentUser.id]

  const {t} = useTranslation()
  const {id} = useParams()
  const [pdfUrl, setPdfUrl] = useState<string>('')

  const {data: templateData} = useTemplate(id)
  const {mutate: updateTemplateMutation} = useUpdateTemplate()
  const {mutate: createTemplateMutation} = useCreateTemplate()

  useEffect(
    () =>
      setTitleLinks([
        {title: t(TranslationKey['Admin panel']), link: NavigationPathKey.ADMIN_PANEL},
        {title: t(TranslationKey.Contracts), link: NavigationPathKey.CONTRACT_DASHBOARD},
        {title: t(TranslationKey['Contract template search']), link: NavigationPathKey.CONTRACT_TEMPLATE_SEARCH},
        {title: id ? t(TranslationKey['Edit contract template']) : t(TranslationKey['New contract template'])},
      ]),
    [id],
  )

  useEffect(() => {
    if (id && templateData) {
      setContractTemplateData(templateData)
    } else {
      setContractTemplateData(prev => ({...prev, companyId: userSelection.companyId}))
    }
  }, [id, templateData, userSelection])

  /**
   * A function handling Contract Template form fields change
   * @param event Change event
   */
  const onChangeContractTemplateFormField = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setIsFormDirty(true)
    const fieldName = event.target.name
    const value = event.target.value
    setContractTemplateFormFieldsErrors(prev => prev.filter(item => item !== fieldName))
    setContractTemplateData(prev => ({...prev, [fieldName]: value}))
  }

  /**
   * A function handling Contract Type dropdown form fields change
   * @param fieldName Form field name
   * @param item Dropdown item
   */
  const onChangeContractTemplateDropdownFormField = (fieldName: string, item: DropdownItem | null) => {
    setIsFormDirty(true)
    setContractTemplateFormFieldsErrors(prev => prev.filter(item => item !== fieldName))
    setContractTemplateData(prev => ({...prev, [fieldName]: item?.value ?? null}))
  }

  /**
   * Contract Template form fields configuration
   */
  const contractTemplateFormFields: FormFieldItem[] = [
    {
      fieldType: FormFieldType.INPUT,
      label: t(TranslationKey['Contract template name']),
      name: 'name',
      onChange: onChangeContractTemplateFormField,
      value: contractTemplateData.name,
      error: contractTemplateFormFieldsErrors.includes('name'),
    },
    {
      fieldType: FormFieldType.INPUT,
      label: t(TranslationKey.Description),
      name: 'description',
      onChange: onChangeContractTemplateFormField,
      value: contractTemplateData.description,
      error: contractTemplateFormFieldsErrors.includes('description'),
    },
    {
      fieldType: FormFieldType.DROPDOWN,
      label: t(TranslationKey['Contract type']),
      ...(contractTemplateData.contractType === 'TA'
        ? {tooltip: t(TranslationKey['Make sure you only have one Transfer Agreement template'])}
        : {}),
      items: [...contractTypeOptions(), {label: t(TranslationKey['Transfer Agreement']), value: 'TA'}],
      name: 'contractType',
      onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
        onChangeContractTemplateDropdownFormField('contractType', {label: '', value: event.target.value}),
      value: contractTemplateData.contractType,
      error: contractTemplateFormFieldsErrors.includes('contractType'),
    },
  ]

  /**
   * A custom validation function for Contract Template form data
   * @returns If Contract Type form field containt any invalid fields
   */
  const validateData = (): boolean => {
    const notRequiredFields = ['id']
    const invalidValues = validateFields(contractTemplateData).filter(item => !notRequiredFields.includes(item))
    const isFormInvalid = invalidValues.length > 0
    if (isFormInvalid) {
      setError({
        message: generateReadableErrorMessages(t, invalidValues),
        name: '',
      })

      setContractTemplateFormFieldsErrors(invalidValues)
    }
    return isFormInvalid
  }

  const validateHtml = (htmlString: string): string[] => {
    const errors: string[] = []

    const requiredTemplateLiterals: string[] = []

    requiredTemplateLiterals.forEach(item => {
      if (!htmlString.includes(item)) errors.push(item)
    })

    return errors
  }

  const handleHtmlDrop = (acceptedFiles: File[]) => {
    const file = acceptedFiles[0]
    const reader = new FileReader()

    reader.onload = () => {
      const result = reader.result as string

      const errors = validateHtml(result)

      if (!errors.length) {
        setContractTemplateData(prev => ({...prev, htmlContent: result}))
      } else {
        setError({
          message: `${t(TranslationKey['Missing required parameters in the HTML'])}: ${errors.map(item => templateLiterals.find(template => template.key === item)?.name).join(', ')}.`,
          name: '',
        })
      }
    }

    if (file && (file.type === 'text/html' || file.type === 'text/plain')) {
      reader.readAsText(file)
    } else {
      setError({
        message: t(TranslationKey['Invalid file format']),
        name: '',
      })
    }
  }

  const handleTemplateDownload = () => {
    const content = templateHtml
    const blob = new Blob([content], {type: 'text/plain;charset=utf-8'})
    setIsUploadDisabled(false)
    saveAs(blob, 'contract-template.txt')
  }

  /**
   * A function handling Save button click
   * If data is valid updated or saves a new Contract Template
   * @returns void
   */
  const onSubmit = () => {
    if (validateData()) return

    const clearAndExit = (id: string) => {
      setIsFormDirty(false)
      navigate(`${NavigationPathKey.CONTRACT_TEMPLATE_VIEW}/${id}`)
    }

    if (id) {
      updateTemplateMutation(
        {
          id,
          body: contractTemplateData as BaseTemplateData,
        },
        {onSuccess: () => clearAndExit(id)},
      )
    } else {
      createTemplateMutation(contractTemplateData as BaseTemplateData, {
        onSuccess: ({id}) => clearAndExit(id),
      })
    }
  }

  /**
   * A function handling Discard button click
   */
  const onDiscard = (): void => {
    handleNavigate(() => navigate(`${NavigationPathKey.CONTRACT_TEMPLATE_VIEW}/${id}`))
  }

  /**
   * Download should only be enabled when a Company is selected
   */
  const isDownloadDisabled = useMemo(() => !contractTemplateData.companyId, [contractTemplateData])

  const htmlContent = useMemo(() => contractTemplateData.htmlContent, [contractTemplateData])

  const templateLiterals = [
    {name: t(TranslationKey['Company name']), key: 'COMPANY_NAME'},
    {name: t(TranslationKey['Company logo']), key: 'COMPANY_LOGO'},
    {name: t(TranslationKey['Creation date']), key: 'CREATION_DATE'},
    {name: t(TranslationKey['Contract name']), key: 'CONTRACT_NAME'},
    {name: t(TranslationKey['Contract code']), key: 'CONTRACT_CODE'},
    {name: t(TranslationKey['Official signatory']), key: 'OFFICIAL_SIGNATORY'},
    {name: t(TranslationKey['Customer first name']), key: 'CUSTOMER_FIRST_NAME'},
    {name: t(TranslationKey['Customer last name']), key: 'CUSTOMER_LAST_NAME'},
    {name: t(TranslationKey['Customer personal no']), key: 'CUSTOMER_PERSONAL_NO'},
    {name: t(TranslationKey['Customer email']), key: 'CUSTOMER_EMAIL'},
    {name: t(TranslationKey['Customer phone']), key: 'CUSTOMER_PHONE'},
    {name: t(TranslationKey['Customer street']), key: 'CUSTOMER_STREET'},
    {name: t(TranslationKey['Customer zip code']), key: 'CUSTOMER_ZIP_CODE'},
    {name: t(TranslationKey['Customer city']), key: 'CUSTOMER_CITY'},
    {name: t(TranslationKey['Dealer name']), key: 'DEALER_NAME'},
    {name: t(TranslationKey['Dealer VAT number']), key: 'DEALER_VAT_NUMBER'},
    {name: t(TranslationKey['Dealer company number']), key: 'DEALER_COMPANY_NO'},
    {name: t(TranslationKey['Dealer phone']), key: 'DEALER_PHONE'},
    {name: t(TranslationKey['Dealer number']), key: 'DEALER_NO'},
    {name: t(TranslationKey['Dealer address']), key: 'DEALER_ADDRESS'},
    {name: t(TranslationKey['Dealer zip code']), key: 'DEALER_ZIP_CODE'},
    {name: t(TranslationKey['Dealer city']), key: 'DEALER_CITY'},
    {name: t(TranslationKey['Product quantity']), key: 'PRODUCT_QTY'},
    {name: t(TranslationKey['Product name']), key: 'PRODUCT_NAME'},
    {name: t(TranslationKey['Product serial no']), key: 'PRODUCT_SERIAL_NO'},
    {name: t(TranslationKey['Product insurance']), key: 'PRODUCT_INSURANCE'},
    {name: t(TranslationKey['Product service']), key: 'PRODUCT_SERVICE'},
    {name: t(TranslationKey['Product residual']), key: 'PRODUCT_RESIDUAL'},
    {name: t(TranslationKey['Product monthly fee']), key: 'PRODUCT_MONTHLY_FEE'},
    {name: t(TranslationKey['Product total price']), key: 'PRODUCT_TOTAL_PRICE'},
    {name: t(TranslationKey['Contract length']), key: 'TOTAL_LENGTH'},
    {name: t(TranslationKey['Contract monthly fee']), key: 'TOTAL_MONTHLY_FEE'},
    {name: t(TranslationKey['Contract total']), key: 'TOTAL_TOTAL'},
    {name: t(TranslationKey['Terms and disclaimer']), key: 'TERMS_DISCLAIMER'},
  ]

  const mockPreviewData: Record<string, string> = {
    COMPANY_NAME: 'My Company Ltd.',
    COMPANY_LOGO: placeholder,
    CREATION_DATE: '2025-07-28',
    CONTRACT_NAME: 'Contract #12345',
    CUSTOMER_FIRST_NAME: 'John',
    CUSTOMER_LAST_NAME: 'Doe',
    CUSTOMER_EMAIL: 'john.doe@example.com',
    CUSTOMER_PHONE: '+1234567890',
    CUSTOMER_STREET: '123 Main St',
    CUSTOMER_ZIP_CODE: '12345',
    CUSTOMER_CITY: 'Sample City',
    DEALER_VAT_NUMBER: 'DE123456789',
    DEALER_COMPANY_NO: '987654321',
    DEALER_NO: '123456',
    DEALER_ADDRESS: '456 Dealer St',
    DEALER_ZIP_CODE: '54321',
    DEALER_CITY: 'Dealer City',
    PRODUCT_QTY: '10',
    PRODUCT_NAME: 'Sample Product',
    PRODUCT_SERIAL_NO: 'SN1234567890',
    PRODUCT_INSURANCE: 'Full Coverage',
    PRODUCT_SERVICE: '2 Years Warranty',
    PRODUCT_RESIDUAL: '$500',
    PRODUCT_MONTHLY_FEE: '$50',
    PRODUCT_TOTAL_PRICE: '$500',
    TOTAL_LENGTH: '24 Months',
    TOTAL_MONTHLY_FEE: '$50',
    TOTAL_TOTAL: '$1200',
    TERMS_DISCLAIMER:
      'Terms and conditions apply. Subject to change. Sed ut perspiciatis unde omnis iste natus error sit voluptatem accusantium doloremque laudantium, totam rem aperiam, eaque ipsa quae ab illo inventore veritatis et quasi architecto beatae vitae dicta sunt explicabo. Nemo enim ipsam voluptatem quia voluptas sit aspernatur aut odit aut fugit, sed quia consequuntur magni dolores eos qui ratione voluptatem sequi nesciunt. Neque porro quisquam est, qui dolorem ipsum quia dolor sit amet, consectetur, adipisci velit, sed quia non numquam eius modi tempora incidunt ut labore et dolore magnam aliquam quaerat voluptatem. Ut enim ad minima veniam, quis nostrum exercitationem ullam corporis suscipit laboriosam, nisi ut aliquid ex ea commodi consequatur? Quis autem vel eum iure reprehenderit qui in ea voluptate velit esse quam nihil molestiae consequatur, vel illum qui dolorem eum fugiat quo voluptas nulla pariatur?',
  }

  const replaceTemplateKeys = async () => {
    const htmlData = htmlContent.replace(/\$\{(.*?)\}/g, (_, key) => {
      const cleanKey = key.trim()

      return mockPreviewData[cleanKey] || `\${${cleanKey}}`
    })
    const doc = await generatePdf(htmlData)
    const pdfWithNumbers = addTopRightPageNumbers(
      doc,
      (i, total) => `${t(TranslationKey.Page)} ${i} ${t(TranslationKey.of)} ${total}`,
    )
    const pdfBlob = pdfWithNumbers.output('blob')
    const pdfUrl = URL.createObjectURL(pdfBlob)
    setPdfUrl(pdfUrl)
  }

  useEffect(() => {
    replaceTemplateKeys()
  }, [htmlContent])

  return {
    onDiscard,
    onSubmit,
    handleHtmlDrop,
    handleTemplateDownload,
    isUploadDisabled,
    t,
    isDownloadDisabled,

    contractTemplateData,
    contractTemplateFormFields,
    contractTemplateFormFieldsErrors,
    templateLiterals,
    pdfUrl,
    id,
  }
}
